import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const BACKEND =
  (import.meta as any).env?.VITE_BACKEND_API_URL ||
  'https://fcc-summarize-app-r4ac3.ondigitalocean.app';

interface ExParteItem {
  submission_id: string;
  filer: string;
  date: string;
  position: string;
}
interface Branding {
  display_name?: string;
  primary_color?: string;
  logo_url?: string;
  address_block?: string;
  upl_disclaimer?: string;
}
interface LandingData {
  proceeding_name?: string;
  branding?: Branding;
  items?: ExParteItem[];
}

/**
 * Client-facing, firm-branded ex-parte advocacy page — no app chrome.
 * The digest "view all ex partes" link points here (/d/{firm}/{docket}).
 */
export function FirmExParteLandingPage() {
  const { firmSlug, docketNumber } = useParams();
  const [data, setData] = useState<LandingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetch(`${BACKEND}/api/firm/${encodeURIComponent(firmSlug!)}/ex-parte/${encodeURIComponent(docketNumber!)}`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) { setData(null); setFailed(true); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [firmSlug, docketNumber]);

  const b = data?.branding || {};
  const accent = b.primary_color || '#1a2332';
  const items = data?.items || [];

  useEffect(() => {
    document.title = b.display_name
      ? `Ex Parte Advocacy — ${b.display_name}`
      : 'Ex Parte Advocacy';
  }, [b.display_name]);

  return (
    <div style={{ minHeight: '100vh', background: '#f5f6f8', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <div style={{ background: accent, padding: '28px 20px' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>
          {b.logo_url ? (
            <img src={b.logo_url} alt={b.display_name || ''} style={{ maxHeight: 40, maxWidth: 240, display: 'block', marginBottom: 8 }} />
          ) : (
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{b.display_name}</div>
          )}
          <h1 style={{ color: '#fff', fontFamily: 'Georgia, serif', fontSize: 24, fontWeight: 700, margin: '6px 0 0' }}>
            Ex Parte Advocacy
          </h1>
        </div>
      </div>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 20px' }}>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#1a2332' }}>{data?.proceeding_name || docketNumber}</div>
          <div style={{ color: '#8894a6', fontSize: 14, marginTop: 2 }}>
            Docket {docketNumber} · {items.length} ex parte filing{items.length === 1 ? '' : 's'} · who lobbied the Commission and what they urged
          </div>
        </div>

        {loading ? (
          <p style={{ color: '#8894a6' }}>Loading ex parte advocacy…</p>
        ) : failed ? (
          <p style={{ color: '#8894a6' }}>We couldn't load this page right now. Please refresh in a moment.</p>
        ) : items.length === 0 ? (
          <p style={{ color: '#8894a6' }}>No ex parte filings found for this docket.</p>
        ) : (
          items.map((it) => (
            <div key={it.submission_id} style={{ background: '#fff', border: '1px solid #e5e8ec', borderRadius: 8, padding: 16, marginBottom: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
                <span style={{ fontWeight: 700, color: '#1a2332' }}>{it.filer}</span>
                <span style={{ color: '#8894a6', fontSize: 13, whiteSpace: 'nowrap' }}>{it.date}</span>
              </div>
              <p style={{ margin: '6px 0 6px', color: '#3a4657', lineHeight: 1.55 }}>{it.position}</p>
              <a
                href={`https://www.fcc.gov/ecfs/search/search-filings/filing/${it.submission_id}`}
                target="_blank"
                rel="noreferrer"
                style={{ color: accent, fontWeight: 600, fontSize: 13, textDecoration: 'none' }}
              >
                View on ECFS ›
              </a>
            </div>
          ))
        )}

        <div style={{ marginTop: 32, paddingTop: 16, borderTop: '1px solid #e5e8ec', color: '#a6b0bf', fontSize: 12, textAlign: 'center' }}>
          {b.upl_disclaimer && <div style={{ fontStyle: 'italic', marginBottom: 6 }}>{b.upl_disclaimer}</div>}
          {b.address_block && <div>{b.address_block}</div>}
        </div>
      </div>
    </div>
  );
}
